// src/Views/FAQ/FAQ.js
// 🎯 Conversion-Optimized FAQ Page
// Updated: January 22, 2026 - Crypto AI & Polymarket Focus + AI Assistant Premium Features
// Protected methodology - focuses on RESULTS not HOW it works

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import './FAQ.css';

const FAQ = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [openItems, setOpenItems] = useState({});

    const toggleItem = (id) => {
        setOpenItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // FAQ Data - Strategically ordered for conversion
    const faqSections = [
        {
            title: "About AiPredict",
            icon: "solar:cpu-bolt-bold",
            color: "#3b82f6",
            questions: [
                {
                    id: "what-is",
                    q: "What is AiPredict and how does it work?",
                    a: `AiPredict is an AI-powered platform for football predictions and crypto auto-trading.

**Football AI:** Our Premium AI Assistant (V10, HRHR, Basketball) analyzes matches on demand with high-accuracy prediction engines. Build your own betting slips with AI-powered analysis.

**Ignis AI Agent:** Ignis connects to your Bybit account and trades crypto automatically — trend detection, precision entries, and multi-tier take profits. No manual trading needed.

We've invested significant resources building and training our models—it's not a generic tool, it's purpose-built for predictions and trading.`
                },
                {
                    id: "different",
                    q: "What makes AiPredict different from other platforms?",
                    a: `Most signal providers rely on basic technical analysis or "influencer calls." AiPredict is fundamentally different:

• **Real Auto-Trading** — Ignis AI Agent executes trades on your account automatically, not just signals you have to follow
• **Purpose-Built AI** — We use real AI models specifically trained for market prediction and football analysis
• **Transparent Results** — Every signal and trade is logged and verifiable
• **Risk-First Approach** — Smart stop losses, breakeven protection, and multi-tier take profits built in
• **Multiple Engines** — V10 for football, HRHR for value bets, Basketball V4, and Ignis`
                },
                {
                    id: "what-markets",
                    q: "What markets does AiPredict cover?",
                    a: `AiPredict covers two main areas:

**1. Ignis**
• Ignis trades crypto on your Bybit account automatically
• Covers BTC, ETH, SOL, XRP, and more — 24/7
• Trend detection, AI-reviewed entries, 3-tier take profits
• Three plans: Bronze ($500/mo), Silver ($1,000/mo), Gold ($2,000/mo)

**2. Football AI Assistant (Premium Subscription)**
• V10 Engine — High-accuracy match analysis on demand
• HRHR (High Risk High Reward) — Value betting opportunities
• Basketball V4 — NBA and EuroLeague analysis`
                }
            ]
        },
        {
            title: "Ignis",
            icon: "solar:bitcoin-circle-bold",
            color: "#fbbf24",
            questions: [
                {
                    id: "crypto-how",
                    q: "How does Ignis AI Agent work?",
                    a: `Ignis AI Agent connects to your Bybit account via API and trades crypto automatically on your behalf.

**How It Works:**
• You provide read-only trade API keys (no withdrawal access)
• Ignis AI Agent monitors markets 24/7
• When it detects high-probability setups, it enters trades automatically
• Multi-tier take profits (TP1, TP2, TP3) lock in gains progressively
• Smart stop losses and breakeven protection manage risk

**What Ignis Trades:**
• BTC, ETH, SOL, XRP and more
• Trend detection and AI-reviewed entries
• Multiple timeframes for maximum opportunity
• Automated position sizing based on your tier`
                },
                {
                    id: "crypto-tiers",
                    q: "What are the Ignis subscription tiers?",
                    a: `Ignis has three tiers based on your trading capital:

**🥉 Bronze — $500/month**
• Up to $2,000 trading capital
• All assets and timeframes
• Telegram trade alerts
• Dashboard access

**🥈 Silver — $1,000/month**
• Up to $5,000 trading capital
• Everything in Bronze
• Higher position sizes

**🥇 Gold — $2,000/month**
• Up to $10,000 trading capital
• Maximum position sizes
• Priority support

All tiers get the same AI strategy — the difference is capital allocation. Payment is in USDT (TRC-20).`
                },
                {
                    id: "crypto-risk",
                    q: "Is Ignis safe? What about risk management?",
                    a: `Security and risk management are built into every layer:

**API Security:**
• Your API keys are encrypted with AES-256 military-grade encryption
• Keys are set to "Trade Only — No Withdrawals"
• Your funds stay in YOUR Bybit wallet at all times
• We cannot withdraw your funds — ever

**Trading Risk Management:**
• Smart stop losses on every trade
• Breakeven protection after TP1 hits
• Daily loss circuit breaker halts trading if stops pile up
• Asset cooldown prevents overtrading one pair
• Profit lock protects gains during hot streaks

**Important:** All trading involves risk. Past performance doesn't guarantee future results. Never allocate capital you can't afford to lose.`,
                    cta: { label: "Read Profit Guide", path: "/guide" }
                }
            ]
        },
        {
            title: "AI Assistant (Premium)",
            icon: "solar:rocket-bold",
            color: "#f59e0b",
            questions: [
                {
                    id: "ai-assistant",
                    q: "What is the AI Assistant?",
                    a: `The AI Assistant is an interactive prediction engine available exclusively to Premium subscribers.

Instead of waiting for daily signals, you can input specific matches or scenarios and get AI-powered analysis on demand.

**Two Engines Available:**

**V10 Engine**
• Our most accurate football prediction model
• Analyzes form, H2H, injuries, league standings
• Provides match predictions with confidence ratings
• Best for: Pre-match analysis and singles betting

**HRHR Engine (High Risk High Reward)**
• Specialized for value betting opportunities
• Identifies mispriced markets where bookmakers have it wrong
• Higher odds, calculated risk
• Best for: Finding value bets with edge`
                },
                {
                    id: "v10-accuracy",
                    q: "How accurate is the V10 Engine?",
                    a: `V10 is our most sophisticated football prediction model, designed for high-probability selections.

**What V10 Does:**
• Analyzes thousands of data points per match
• Considers form, head-to-head, injuries, league context
• Focuses on safe, consistent markets (not risky accumulators)
• Outputs structured predictions with confidence levels

**Key Features:**
• XML-optimized prompts for consistent output
• Internal filters to avoid volatile teams and trap bets
• Clean, user-friendly results (no technical jargon)

We can't share the exact methodology—that's our competitive edge—but V10 is purpose-built for football prediction, not a generic AI.`
                },
                {
                    id: "hrhr-explain",
                    q: "What is HRHR and how is it different from V10?",
                    a: `HRHR (High Risk High Reward) is our value betting engine—different philosophy from V10.

**V10 Philosophy:** Safety first. High-probability, lower-odds selections. Consistent small wins.

**HRHR Philosophy:** Find VALUE. Identify where bookmakers have mispriced markets. Higher odds, calculated edge.

**HRHR Specializes In:**
• Pattern break situations (teams due for results)
• Quality mismatches where odds are wrong
• Specific market types where our model has edge

**HRHR Limits:**
• 3 searches per day (Premium only)
• Maximum 3 matches per search
• Maximum 3-leg accumulators

**Important:** HRHR is higher risk by design. Use smaller stakes and expect some losses. The edge compounds over time, not on every bet.`,
                    cta: { label: "View Pricing", path: "/pricing" }
                },
                {
                    id: "basketball",
                    q: "Is there a Basketball engine?",
                    a: `Yes! Basketball V4 is available in the AI Assistant for Premium users.

**What It Covers:**
• NBA games
• EuroLeague and European domestic leagues

**Analysis Includes:**
• Team form and rest schedules
• Back-to-back game fatigue (critical for NBA)
• Head-to-head patterns
• Injury impact assessment
• Spread and totals recommendations

Basketball V4 is available alongside V10 and HRHR in the AI Assistant for Premium subscribers.`
                }
            ]
        },
        {
            title: "Premium Value",
            icon: "solar:crown-bold",
            color: "#a855f7",
            questions: [
                {
                    id: "worth-it",
                    q: "Is the Premium subscription worth it?",
                    a: `The Premium subscription gives you access to our Football AI engines:

**What You Get:**
• V10 Engine — 4 searches/day, 3 matches each
• HRHR Engine — 3 searches/day, value betting
• Basketball V4 — 3 searches/day, NBA & EuroLeague
• Priority support

**The Math:**
One winning bet covering your subscription cost is common with V10's accuracy. If you bet regularly, the subscription pays for itself quickly.

**Note:** Ignis AI Agent is a separate service with its own pricing (paid in USDT). Premium subscription covers Football AI features only.`
                },
                {
                    id: "premium-get",
                    q: "What exactly do Premium members get?",
                    a: `**AI Assistant Engines:**
✅ V10 Engine — 4 searches/day, 3 matches per search
✅ HRHR Engine — 3 searches/day, 3 matches per search  
✅ Basketball V4 — 3 searches/day, 2 matches per search

**Support & Access:**
✅ Priority customer support
✅ Early access to new features
✅ Risk management guides

**Ignis AI Agent (Separate):**
Ignis AI Agent is a separate service available to any logged-in user — no Premium required. Ignis pricing starts at $500/month paid in USDT.`
                },
                {
                    id: "see-results",
                    q: "Can I see real results before subscribing?",
                    a: `Absolutely. Transparency is core to what we do.

**Football AI:** Visit our Results page to see timestamped predictions with actual outcomes. No cherry-picking—every resolved analysis is shown.

**Ignis AI Agent:** Once enrolled, your dashboard shows real-time trades, win rate, and PnL. Join the Telegram group to see live trade alerts.

We're confident in what we've built. That's why we let the results speak.`,
                    cta: { label: "View Results", path: "/results" }
                }
            ]
        },
        {
            title: "Trust & Credibility",
            icon: "solar:shield-check-bold",
            color: "#4ade80",
            questions: [
                {
                    id: "scam",
                    q: "How do I know this isn't just another scam?",
                    a: `Fair question—the signal space is full of scammers. Here's why we're different:

**1. Real Product, Real App**
We're on the iOS App Store. Apple's review process filters out scams. We have a real product with real users.

**2. Verifiable Signals**
Our Results page shows every signal with timestamps. Cross-reference with actual market outcomes yourself.

**3. Risk-First Messaging**
We emphasize risk management, not "guaranteed profits." Scammers promise the moon—we give you tools and education.

**4. Cancel Anytime**
No contracts, no hidden fees. Not satisfied? Cancel in one click.

**5. Real Support**
Actual humans respond to your questions, typically within hours.

Scammers don't build iOS apps, log results publicly, emphasize risk management, or offer transparent pricing. We do all four.`
                },
                {
                    id: "guarantee",
                    q: "Do you guarantee profits?",
                    a: `**No—and run from anyone who does.**

Trading and betting involve risk. No system can guarantee profits. Anyone claiming otherwise is lying or selling you a scam.

**What we DO offer:**
• **Transparent results** — Every trade logged, every outcome shown
• **Legitimate AI technology** — Real models, not random generators
• **Risk management built-in** — Stop losses, breakeven protection, profit locks
• **Clear pricing** — No hidden fees, cancel anytime

Our job is to give you high-probability signals with clear risk parameters. Your job is to manage your capital responsibly. Together, that's how consistent results happen over time.`
                },
                {
                    id: "cancel",
                    q: "Can I cancel anytime?",
                    a: `**Yes, instantly.**

• No contracts
• No cancellation fees  
• No "call us to cancel" games

Cancel from your Profile or app settings. Access continues until your billing period ends, then stops. Simple.

We'd rather earn your subscription monthly by delivering value than trap you with fine print.`
                }
            ]
        },
        {
            title: "Getting Started",
            icon: "solar:play-bold",
            color: "#38bdf8",
            questions: [
                {
                    id: "use-signals",
                    q: "How do I use Ignis AI Agent?",
                    a: `Getting started with Ignis is simple:

**Step 1: Create a Bybit Account**
Sign up at Bybit.com if you don't have one already.

**Step 2: Create API Keys**
Go to Bybit API Management and create a key with "Trade Only — No Withdrawals" permissions.

**Step 3: Choose Your Tier**
Visit the Crypto AI page in AiPredict, select your tier (Bronze/Silver/Gold), and paste your API keys.

**Step 4: Send Payment**
Send USDT to the provided wallet address (TRC-20 network). Your account will be activated within 24 hours.

**Step 5: Sit Back**
Ignis starts trading automatically. Monitor your trades in the dashboard and join the Telegram group for real-time alerts.

**Key Rules:**
• Do NOT manually trade the same assets Ignis is managing
• Keep sufficient USDT balance in your Bybit account
• Ignis handles entries, exits, and risk management automatically`
                },
                {
                    id: "ai-assistant-use",
                    q: "How do I use the AI Assistant?",
                    a: `The AI Assistant is your on-demand prediction engine.

**Steps:**
1. Go to AI Assistant in the app
2. Select your engine (V10, HRHR, or Basketball)
3. Enter the matches or scenarios you want analyzed
4. Get structured predictions with confidence ratings

**Tips:**
• Be specific with match names (e.g., "Liverpool vs Arsenal")
• V10 is best for pre-match analysis
• HRHR is best for finding value bets
• Basketball V4 for NBA/EuroLeague

**Limits:**
• V10: 4 searches/day, 3 matches each
• HRHR: 3 searches/day, 3 matches each
• Basketball: 3 searches/day, 2 matches each`
                },
                {
                    id: "get-started",
                    q: "How do I get started?",
                    a: `**For Football AI (Premium Subscription):**

1. Create an account — takes 30 seconds
2. Go to Pricing and subscribe (weekly/monthly/yearly)
3. Open the AI Assistant and start analyzing matches

**For Ignis:**

1. Create an account on AiPredict
2. Set up a Bybit account with API keys (Trade Only)
3. Visit the Crypto AI page and submit your keys
4. Send USDT payment for your chosen tier
5. Ignis starts trading within 24 hours

Both services work independently — you can use one or both.`,
                    cta: { label: "View Pricing", path: "/pricing" }
                }
            ]
        }
    ];

    return (
        <section className="faq">
            <div className="faq__inner">
                {/* Header */}
                <div className="faq__header">
                    <div className="faq__eyebrow">
                        <Icon icon="solar:question-circle-bold" className="faq__eyebrow-icon" />
                        <span>Frequently Asked Questions</span>
                    </div>
                    <h1 className="faq__title">
                        Everything You Need to Know About{' '}
                        <span className="faq__title-highlight">AiPredict</span>
                    </h1>
                    <p className="faq__subtitle">
                        Real answers about our platform, signals, and AI-powered prediction engines.
                    </p>
                </div>

                {/* Stats Banner */}
                <div className="faq__stats-banner">
                    <div className="faq__stat">
                        <span className="faq__stat-value">Crypto Bot</span>
                        <span className="faq__stat-label">Auto-Trading</span>
                    </div>
                    <div className="faq__stat-divider" />
                    <div className="faq__stat">
                        <span className="faq__stat-value">3 Tiers</span>
                        <span className="faq__stat-label">Bronze · Silver · Gold</span>
                    </div>
                    <div className="faq__stat-divider" />
                    <div className="faq__stat">
                        <span className="faq__stat-value">V10 + HRHR</span>
                        <span className="faq__stat-label">AI Assistant</span>
                    </div>
                    <div className="faq__stat-divider" />
                    <div className="faq__stat">
                        <span className="faq__stat-value">3-Day</span>
                        <span className="faq__stat-label">Free Trial</span>
                    </div>
                </div>

                {/* FAQ Sections */}
                <div className="faq__sections">
                    {faqSections.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="faq__section">
                            <div className="faq__section-header">
                                <div 
                                    className="faq__section-icon" 
                                    style={{ background: `${section.color}20`, color: section.color }}
                                >
                                    <Icon icon={section.icon} />
                                </div>
                                <h2 className="faq__section-title">{section.title}</h2>
                            </div>

                            <div className="faq__questions">
                                {section.questions.map((item) => (
                                    <div 
                                        key={item.id} 
                                        className={`faq__item ${openItems[item.id] ? 'faq__item--open' : ''}`}
                                    >
                                        <button 
                                            className="faq__question"
                                            onClick={() => toggleItem(item.id)}
                                        >
                                            <span>{item.q}</span>
                                            <Icon 
                                                icon={openItems[item.id] ? "solar:minus-circle-bold" : "solar:add-circle-bold"} 
                                                className="faq__toggle-icon"
                                            />
                                        </button>
                                        
                                        {openItems[item.id] && (
                                            <div className="faq__answer">
                                                <div className="faq__answer-content">
                                                    {item.a.split('\n').map((paragraph, i) => (
                                                        <p key={i}>{paragraph}</p>
                                                    ))}
                                                </div>
                                                
                                                {item.cta && (
                                                    <button 
                                                        className="faq__cta-btn"
                                                        onClick={() => navigate(item.cta.path)}
                                                    >
                                                        <Icon icon="solar:arrow-right-bold" />
                                                        <span>{item.cta.label}</span>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className="faq__bottom-cta">
                    <div className="faq__cta-content">
                        <h3 className="faq__cta-title">Ready to Start Trading Smarter?</h3>
                        <p className="faq__cta-text">
                            Get AI-powered signals for crypto and Polymarket, plus access to our prediction engines.
                        </p>
                    </div>
                    <div className="faq__cta-buttons">
                        {!user ? (
                            <button 
                                className="faq__btn faq__btn--primary"
                                onClick={() => navigate('/signup')}
                            >
                                <Icon icon="solar:user-plus-bold" />
                                <span>Create Free Account</span>
                            </button>
                        ) : (
                            <button 
                                className="faq__btn faq__btn--primary"
                                onClick={() => navigate('/pricing')}
                            >
                                <Icon icon="solar:crown-bold" />
                                <span>Start Free Trial</span>
                            </button>
                        )}
                        <button 
                            className="faq__btn faq__btn--ghost"
                            onClick={() => navigate('/results')}
                        >
                            <Icon icon="solar:chart-bold" />
                            <span>View Results</span>
                        </button>
                    </div>
                </div>

                {/* Still Have Questions */}
                <div className="faq__support">
                    <Icon icon="solar:chat-round-dots-bold" className="faq__support-icon" />
                    <div className="faq__support-content">
                        <h4>Still have questions?</h4>
                        <p>Our support team typically responds within 2 hours.</p>
                    </div>
                    <button 
                        className="faq__support-btn"
                        onClick={() => navigate('/support')}
                    >
                        Contact Support
                    </button>
                </div>
            </div>
        </section>
    );
};

export default FAQ;